/**
 * SprintHealthBanner
 *
 * Prominent banner above the sprint telemetry tab bar.
 * Surfaces the root cause of build failures so users don't have to dig through logs.
 *
 * - Hidden: No errors
 * - Warning (amber): Errors exist but diverse
 * - Critical (red): Root cause affects multiple stories
 */

import { useState } from 'react';
import { AlertTriangle, XCircle, X } from 'lucide-react';
import { useSprintErrorSummary, type ErrorSummaryResponse } from '../../api/telemetry';

interface SprintHealthBannerProps {
  workflowId: string | undefined;
  totalStories?: number;
  sprintStatus?: string;
}

export default function SprintHealthBanner({ workflowId, totalStories, sprintStatus }: SprintHealthBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  const { data } = useSprintErrorSummary(workflowId);

  if (dismissed) return null;

  // Sprint failed but no telemetry errors recorded — infrastructure-level failure
  if (sprintStatus === 'failed' && (!data || data.totalErrors === 0)) {
    return (
      <div className="rounded-lg border px-4 py-3 flex items-start gap-3 bg-amber-50 border-amber-200">
        <AlertTriangle size={18} className="text-amber-500 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-800">
            Sprint failed with no error events recorded
          </p>
          <p className="text-xs mt-0.5 text-amber-600">
            This usually indicates an infrastructure-level failure (Temporal timeout, network error, or worker crash). Check the History tab for Temporal workflow details.
          </p>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 rounded hover:bg-white/50 shrink-0 text-amber-400 hover:text-amber-600"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  if (!data || data.totalErrors === 0) return null;

  const { topError, errors, totalErrors } = data;
  const isCritical = topError && (topError.severity === 'critical' || topError.count >= 2);
  const affectedCount = topError?.count || 0;
  const affectedLabel = totalStories
    ? `${affectedCount} of ${totalStories} stories affected`
    : `${affectedCount} occurrences`;

  return (
    <div
      className={`rounded-lg border px-4 py-3 flex items-start gap-3 ${
        isCritical
          ? 'bg-red-50 border-red-200'
          : 'bg-amber-50 border-amber-200'
      }`}
    >
      {isCritical ? (
        <XCircle size={18} className="text-red-500 mt-0.5 shrink-0" />
      ) : (
        <AlertTriangle size={18} className="text-amber-500 mt-0.5 shrink-0" />
      )}

      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${isCritical ? 'text-red-800' : 'text-amber-800'}`}>
          {isCritical
            ? `Build Blocked: ${topError?.errorText || 'Unknown error'}`
            : `${errors.length} different errors across ${totalErrors} failures`}
        </p>
        <p className={`text-xs mt-0.5 ${isCritical ? 'text-red-600' : 'text-amber-600'}`}>
          {isCritical
            ? affectedLabel
            : 'Check the History tab for details'}
        </p>
      </div>

      <button
        onClick={() => setDismissed(true)}
        className={`p-1 rounded hover:bg-white/50 shrink-0 ${
          isCritical ? 'text-red-400 hover:text-red-600' : 'text-amber-400 hover:text-amber-600'
        }`}
      >
        <X size={14} />
      </button>
    </div>
  );
}
